/**
 * Product Catalog Page
 *
 * 3-layer folder view of Manifold product config:
 *   item_families → items → item_prices
 *
 * Read-only for everyone; super_admin / clinic_admin on pricing-enabled clinics
 * also get inline edit + create tools (backend gates writes regardless).
 *
 * Editing is always `pricing_mode: 'release'`: an edit is staged as a draft
 * change and nothing reaches Manifold until the release is published, which is
 * what makes the change revertible and auditable.
 *
 * There used to be a second mode, 'direct', for the clinics on the
 * single-tenant Manifold, where a save wrote to Mongo immediately. It was
 * retired on 2026-08-04 when MDExam and MDRX moved and that deployment froze.
 * The field is still read from the response rather than assumed, so the backend
 * stays the authority.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { manifoldAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'
import {
  Card, CardContent,
  Button, Input, Label,
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, ChevronRight, ChevronDown,
  Package, FolderOpen, Folder, Tag, DollarSign, Pencil, Plus
} from 'lucide-react'

function formatCurrency(cents) {
  if (!cents && cents !== 0) return '-'
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Whether a price carries an intro price worth showing.
 *
 * `intro_price` is optional on Manifold's item_price documents. 0 is a real
 * value — a free first period — so this tests for presence rather than
 * truthiness; treating 0 as absent would hide a free offer and, on save, charge
 * for one sold as free.
 */
function hasIntroPrice(price) {
  return price?.intro_price !== null && price?.intro_price !== undefined
}

function formatPeriod(period, unit) {
  if (!period || !unit) return ''
  if (period === 1) return `/ ${unit}`
  return `/ ${period} ${unit}s`
}

const PERIOD_UNITS = ['day', 'week', 'month', 'year']

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  deleted: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[status] || STATUS_BADGE.archived}`}>
      {status}
    </span>
  )
}

/** Build a Chargebee-style suggested id for a new price. */
function suggestPriceId(itemId, currency, period, unit) {
  if (!itemId) return ''
  const cur = (currency || 'USD').toUpperCase()
  let periodLabel
  if (period === 1 && unit === 'month') periodLabel = 'Monthly'
  else if (period === 1 && unit === 'year') periodLabel = 'Yearly'
  else if (period === 1 && unit === 'week') periodLabel = 'Weekly'
  else if (period === 1 && unit === 'day') periodLabel = 'Daily'
  else periodLabel = `Every-${period}-${unit}s`
  return `${itemId}-${cur}-${periodLabel}`
}

function PriceRow({ price, canEdit, onEdit }) {
  return (
    <div className="flex items-center justify-between py-2 px-4 ml-14 border-l-2 border-dashed border-border hover:bg-muted/30 transition-colors rounded-r group">
      <div className="flex items-center gap-3 min-w-0">
        <Tag className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
        <div className="min-w-0">
          <span className="text-sm font-mono text-foreground truncate block">{price.id}</span>
          {price.external_name && price.external_name !== price.name && (
            <span className="text-xs text-muted-foreground">{price.external_name}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        {/* Intro price, when the tenant has one set. Shown alongside the
            recurring price rather than instead of it, because it does not
            replace the recurring rate: a subscription's first invoice uses it
            and every later invoice bills the regular price
            (payments: routers/subscriptions.py, and routers/hyperswitch.py for
            the matching initial payment).

            It is not purely first-invoice, though — _resolve_mit_amount
            (routers/hyperswitch.py) derives the authoritative amount for every
            one-click MIT charge against this price id from intro_price, with no
            notion of "first". So a price used as an upsell target charges the
            intro amount on each such charge. */}
        {hasIntroPrice(price) && (
          <span
            className="text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-400"
            title="Intro price — a subscription's first invoice, and every one-click upsell charged against this price"
          >
            {formatCurrency(price.intro_price)}
            <span className="text-muted-foreground font-normal ml-1">intro</span>
          </span>
        )}
        <span className="text-sm font-semibold tabular-nums">
          {formatCurrency(price.price)}
          <span className="text-muted-foreground font-normal ml-1">{formatPeriod(price.period, price.period_unit)}</span>
        </span>
        <span className="text-xs text-muted-foreground uppercase w-10 text-center">{price.currency_code}</span>
        <span className="text-xs text-muted-foreground w-16 text-right">{price.pricing_model}</span>
        <StatusBadge status={price.status} />
        {canEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(price) }}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
            title="Edit price"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

function ItemRow({ item, canEdit, onEditPrice, onCreatePrice, onEditItem }) {
  const [expanded, setExpanded] = useState(false)
  const hasPrices = item.prices?.length > 0

  return (
    <div>
      <div
        className={`w-full flex items-center justify-between py-2.5 px-4 ml-7 border-l-2 border-border transition-colors rounded-r group ${
          expanded ? 'bg-muted/30' : ''
        } ${hasPrices ? 'hover:bg-muted/50' : ''}`}
      >
        <button
          onClick={() => hasPrices && setExpanded(!expanded)}
          className={`flex items-center gap-3 min-w-0 flex-1 text-left ${hasPrices ? 'cursor-pointer' : 'cursor-default'}`}
        >
          {hasPrices ? (
            expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          ) : (
            <span className="w-4 flex-shrink-0" />
          )}
          <Package className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <div className="text-left min-w-0">
            <span className="text-sm font-medium text-foreground truncate block">{item.name}</span>
            {item.external_name && item.external_name !== item.name && (
              <span className="text-xs text-muted-foreground">{item.external_name}</span>
            )}
          </div>
        </button>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className="text-xs text-muted-foreground">{item.type}</span>
          {hasPrices && (
            <span className="text-xs text-muted-foreground">
              {item.prices.length} price{item.prices.length !== 1 ? 's' : ''}
            </span>
          )}
          <StatusBadge status={item.status} />
          {canEdit && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => { e.stopPropagation(); onEditItem(item) }}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                title="Edit item names"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onCreatePrice(item) }}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                title="Add price"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      {expanded && item.prices.map(p => (
        <PriceRow key={p.id} price={p} canEdit={canEdit} onEdit={onEditPrice} />
      ))}
    </div>
  )
}

function FamilyRow({ family, canEdit, onEditPrice, onCreatePrice, onEditItem }) {
  const [expanded, setExpanded] = useState(false)
  const hasItems = family.items?.length > 0

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => hasItems && setExpanded(!expanded)}
        className={`w-full flex items-center justify-between py-3 px-4 transition-colors ${
          hasItems ? 'hover:bg-muted/50 cursor-pointer' : 'cursor-default'
        } ${expanded ? 'bg-muted/40 border-b border-border' : ''}`}
      >
        <div className="flex items-center gap-3">
          {hasItems ? (
            expanded ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />
          ) : (
            <span className="w-5" />
          )}
          {expanded ? (
            <FolderOpen className="h-5 w-5 text-amber-500" />
          ) : (
            <Folder className="h-5 w-5 text-amber-500" />
          )}
          <div className="text-left">
            <span className="text-base font-semibold text-foreground">{family.name}</span>
            {family.description && (
              <span className="text-sm text-muted-foreground ml-3">{family.description}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Package className="h-3.5 w-3.5" />
            <span>{family.item_count} item{family.item_count !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Tag className="h-3.5 w-3.5" />
            <span>{family.price_count} price{family.price_count !== 1 ? 's' : ''}</span>
          </div>
          <StatusBadge status={family.status} />
        </div>
      </button>
      {expanded && (
        <div className="py-1">
          {family.items.map(item => (
            <ItemRow
              key={item.id}
              item={item}
              canEdit={canEdit}
              onEditPrice={onEditPrice}
              onCreatePrice={onCreatePrice}
              onEditItem={onEditItem}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Single modal handling all three write modes.
 * mode: 'edit-price' | 'create-price' | 'edit-item'
 */
function PricingModal({ modal, onClose, onSaved, mode }) {
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const { addChange, updateChange, findChangeForResource } = useDraftStore()
  const staged = mode === 'release'

  // Seed form when modal opens
  useEffect(() => {
    if (!modal) return
    setError(null)
    if (modal.mode === 'edit-price') {
      const p = modal.price
      setForm({
        priceDollars: (p.price ?? 0) / 100,
        // Empty string means "no intro price", which is distinct from '0'
        // (a free first period). hasIntroPrice tests presence, so a stored 0
        // seeds as '0' rather than collapsing to empty.
        introDollars: hasIntroPrice(p) ? String(p.intro_price / 100) : '',
        status: p.status || 'active',
        external_name: p.external_name || '',
        name: p.name || '',
      })
    } else if (modal.mode === 'create-price') {
      const item = modal.item
      const existingCurrency = item.prices?.[0]?.currency_code || 'USD'
      const period = 1
      const period_unit = 'month'
      setForm({
        item_id: item.id,
        priceDollars: 0,
        introDollars: '',
        period,
        period_unit,
        currency_code: existingCurrency,
        external_name: item.external_name || item.name || '',
        name: '',
        status: 'active',
        id: suggestPriceId(item.id, existingCurrency, period, period_unit),
        idTouched: false,
      })
    } else if (modal.mode === 'edit-item') {
      const it = modal.item
      setForm({ name: it.name || '', external_name: it.external_name || '' })
    }
  }, [modal])

  if (!modal) return null

  const set = (patch) => setForm(prev => {
    const next = { ...prev, ...patch }
    // Keep suggested id in sync until the user edits it manually
    if (modal.mode === 'create-price' && !next.idTouched &&
        (patch.currency_code !== undefined || patch.period !== undefined || patch.period_unit !== undefined)) {
      next.id = suggestPriceId(next.item_id, next.currency_code, Number(next.period), next.period_unit)
    }
    return next
  })

  const centsFromDollars = (d) => Math.round(parseFloat(d || 0) * 100)

  /**
   * Intro price in cents, or null to clear it.
   *
   * The three states Manifold distinguishes have to survive this function:
   * an empty field is null ("no introductory rate"), '0' is 0 (a free first
   * period), and any other number is that amount. Sending 0 for an empty field
   * would give away a first period; sending null for '0' would charge for one
   * sold as free.
   */
  const introCentsOrNull = (d) => {
    if (d === '' || d === null || d === undefined) return null
    const cents = Math.round(parseFloat(d) * 100)
    return Number.isFinite(cents) ? cents : null
  }

  /**
   * Whether the intro price differs from what the price already had.
   *
   * Normalizes the stored side to null so an absent field and an explicit null
   * compare equal — Manifold bills them identically, so the difference is not a
   * change worth staging.
   */
  const introChanged = () => {
    const before = modal.price?.intro_price ?? null
    return introCentsOrNull(form.introDollars) !== before
  }

  /**
   * The edit, expressed once. Both modes need the same payload — one sends it
   * to the API now, the other parks it in the draft store for the release.
   */
  const buildEdit = () => {
    if (modal.mode === 'edit-price') {
      return {
        resource_type: 'item_price',
        resource_id: modal.price.id,
        operation: 'update',
        original_data: modal.price,
        data: {
          price: centsFromDollars(form.priceDollars),
          // Sent only when it actually changed, but sent as null when the
          // change is a clear. Omitting it on a clear would read as "don't
          // touch it" and the intro rate would stay live behind a save that
          // looked successful; including it unconditionally would rewrite
          // `absent` as `null` on every unrelated price edit. Both bill the
          // same, but only one of them is a change anyone asked for.
          ...(introChanged() ? { intro_price: introCentsOrNull(form.introDollars) } : {}),
          status: form.status,
          external_name: form.external_name,
          name: form.name,
        },
      }
    }
    if (modal.mode === 'create-price') {
      const id = form.id?.trim()
      if (!id) throw new Error('A price id is required')
      return {
        resource_type: 'item_price',
        resource_id: id,
        operation: 'create',
        original_data: null,
        data: {
          id,
          item_id: form.item_id,
          price: centsFromDollars(form.priceDollars),
          intro_price: introCentsOrNull(form.introDollars),
          period: Number(form.period),
          period_unit: form.period_unit,
          currency_code: form.currency_code,
          external_name: form.external_name,
          name: form.name || id,
          status: form.status,
        },
      }
    }
    return {
      resource_type: 'item',
      resource_id: modal.item.id,
      operation: 'update',
      original_data: modal.item,
      data: {
        name: form.name,
        external_name: form.external_name,
      },
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const edit = buildEdit()

      if (staged) {
        const description = `${edit.resource_type === 'item_price' ? 'Price' : 'Item'} ${edit.resource_id}`
        // Editing the same price twice before publishing must replace the
        // pending change, not queue a second one: Manifold rejects a changeset
        // that touches the same document twice, and the failure would only
        // surface at publish time.
        const pending = findChangeForResource(edit.resource_type, edit.resource_id)
        if (pending) {
          updateChange(pending.id, {
            // A pending create stays a create — it does not exist to be updated yet.
            operation: pending.operation === 'create' ? 'create' : edit.operation,
            new_data: { ...pending.new_data, ...edit.data },
            description,
          })
        } else {
          addChange({
            resource_type: edit.resource_type,
            resource_id: edit.resource_id,
            operation: edit.operation,
            original_data: edit.original_data,
            new_data: edit.data,
            description,
          })
        }
      }

      onSaved()
      onClose()
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const title = modal.mode === 'edit-price' ? 'Edit Price'
    : modal.mode === 'create-price' ? 'Add Price'
    : 'Edit Item'

  const subtitle = modal.mode === 'edit-price' ? modal.price.id
    : modal.mode === 'create-price' ? `New price for ${modal.item.name}`
    : modal.item.id

  return (
    <Dialog open={!!modal} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="font-mono text-xs break-all">{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {modal.mode === 'create-price' && (
            <div className="space-y-1.5">
              <Label htmlFor="price-id">Price ID</Label>
              <Input
                id="price-id"
                value={form.id || ''}
                onChange={e => set({ id: e.target.value, idTouched: true })}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">Must be unique. Auto-suggested from item + currency + period.</p>
            </div>
          )}

          {(modal.mode === 'edit-price' || modal.mode === 'create-price') && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="price-amount">Price ({form.currency_code || modal.price?.currency_code || 'USD'})</Label>
                  <Input
                    id="price-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.priceDollars ?? ''}
                    onChange={e => set({ priceDollars: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="price-status">Status</Label>
                  <Select value={form.status} onValueChange={v => set({ status: v })}>
                    <SelectTrigger id="price-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">active</SelectItem>
                      <SelectItem value="archived">archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Intro price, editable since payments#312 added it to
                  UPDATABLE_FIELDS on the changeset API. Three states have to
                  round-trip: empty is "no introductory rate" (sent as null),
                  '0' is a free first period, and anything else is that amount.
                  Manifold treats null and 0 as materially different, so the
                  input must not collapse them — see introCentsOrNull. */}
              <div className="space-y-1.5">
                <Label htmlFor="intro-amount">
                  Intro price ({form.currency_code || modal.price?.currency_code || 'USD'})
                  <span className="text-muted-foreground font-normal ml-1">— optional</span>
                </Label>
                <Input
                  id="intro-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="No intro price"
                  value={form.introDollars ?? ''}
                  onChange={e => set({ introDollars: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Charged on a subscription's first invoice, then the recurring price after —
                  and on every one-click upsell against this price. Leave blank for none;
                  0 is a free first period.
                </p>
              </div>

              {modal.mode === 'create-price' && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="price-period">Every</Label>
                    <Input
                      id="price-period"
                      type="number"
                      min="1"
                      value={form.period ?? 1}
                      onChange={e => set({ period: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="price-unit">Unit</Label>
                    <Select value={form.period_unit} onValueChange={v => set({ period_unit: v })}>
                      <SelectTrigger id="price-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PERIOD_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="price-currency">Currency</Label>
                    <Input
                      id="price-currency"
                      value={form.currency_code || ''}
                      onChange={e => set({ currency_code: e.target.value.toUpperCase() })}
                      maxLength={3}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {modal.mode === 'edit-item' && (
            <div className="space-y-1.5">
              <Label htmlFor="item-name">Name</Label>
              <Input
                id="item-name"
                value={form.name || ''}
                onChange={e => set({ name: e.target.value })}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ext-name">Display name (external_name)</Label>
            <Input
              id="ext-name"
              value={form.external_name || ''}
              onChange={e => set({ external_name: e.target.value })}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-md p-2.5">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {staged && (
          <p className="text-xs text-muted-foreground border-t pt-3">
            This is staged as a draft. Nothing changes in Manifold until you publish a release.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {staged ? 'Stage change' : modal.mode === 'create-price' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function ProductCatalogPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [modal, setModal] = useState(null)
  const { changes } = useDraftStore()

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await manifoldAPI.getCatalog()
      setData(res.data?.data || res.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Backend returns pricing_enabled = (clinic enabled) && (write role).
  const canEdit = !!data?.pricing_enabled
  const pricingMode = data?.pricing_mode || 'release'

  // Staged pricing edits that have not been published yet. Shown as a banner
  // because the tree below still reflects what Manifold currently serves —
  // the drafts are not applied until a release goes out.
  const pendingPricingChanges = changes.filter((c) =>
    ['item_family', 'item', 'item_price', 'coupon'].includes(c.resource_type)
  )

  /**
   * Drop archived rows at every level, then prune branches the filter emptied.
   *
   * "Emptied by the filter" is not the same as "legitimately empty": an item
   * with no prices at all is a real thing this page renders, so a node is only
   * pruned when it HAD children and lost all of them to the filter. Otherwise
   * turning the filter on would hide rows that have nothing archived about them.
   */
  const liveCatalog = (data?.catalog || []).map(family => {
    if (showArchived) return family
    if (family.status === 'archived') return null

    const items = (family.items || []).map(item => {
      if (item.status === 'archived') return null
      const prices = (item.prices || []).filter(p => p.status !== 'archived')
      if (item.prices?.length && prices.length === 0) return null
      return { ...item, prices }
    }).filter(Boolean)

    if (family.items?.length && items.length === 0) return null
    return { ...family, items, item_count: items.length }
  }).filter(Boolean)

  // Counts reflect the archived filter but deliberately not the search box:
  // search is transient and having the stat cards jump on every keystroke is
  // noise. The tree below is what answers "what matches my search".
  const visibleCounts = liveCatalog.reduce((acc, f) => ({
    families: acc.families + 1,
    items: acc.items + (f.items?.length || 0),
    prices: acc.prices + (f.items || []).reduce((n, i) => n + (i.prices?.length || 0), 0),
  }), { families: 0, items: 0, prices: 0 })

  const hiddenArchived = showArchived
    ? 0
    : (data?.summary?.prices ?? 0) - visibleCounts.prices

  // Filter catalog by search term
  const filteredCatalog = liveCatalog.map(family => {
    if (!search) return family
    const q = search.toLowerCase()
    const matchedItems = family.items?.filter(item => {
      const itemMatch = item.name?.toLowerCase().includes(q)
        || item.external_name?.toLowerCase().includes(q)
        || item.id?.toLowerCase().includes(q)
      const priceMatch = item.prices?.some(p =>
        p.id?.toLowerCase().includes(q) || p.external_name?.toLowerCase().includes(q)
      )
      return itemMatch || priceMatch
    }) || []
    const familyMatch = family.name?.toLowerCase().includes(q) || family.id?.toLowerCase().includes(q)
    if (familyMatch) return family
    if (matchedItems.length === 0) return null
    return { ...family, items: matchedItems, item_count: matchedItems.length }
  }).filter(Boolean)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading product catalog...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" /> Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Product Catalog</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Chargebee product configuration: {visibleCounts.families} families, {visibleCounts.items} items, {visibleCounts.prices} prices
            {canEdit && (
              <span className="ml-2 text-emerald-600 dark:text-emerald-400">
                · {pricingMode === 'release' ? 'edits publish with a release' : 'editing enabled'}
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Staged-but-unpublished pricing edits */}
      {pendingPricingChanges.length > 0 && (
        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
          <div className="text-sm">
            <span className="font-medium text-amber-900 dark:text-amber-100">
              {pendingPricingChanges.length} unpublished pricing{' '}
              {pendingPricingChanges.length === 1 ? 'change' : 'changes'}
            </span>
            <span className="text-amber-800 dark:text-amber-200 ml-2">
              — the catalog below still shows what Manifold is serving today.
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/releases')}>
            Review &amp; publish
          </Button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Folder className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{visibleCounts.families}</p>
                <p className="text-sm text-muted-foreground">Item Families</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{visibleCounts.items}</p>
                <p className="text-sm text-muted-foreground">Items (Plans)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{visibleCounts.prices}</p>
                <p className="text-sm text-muted-foreground">Price Configs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search + archived filter */}
      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder="Search families, items, or prices..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 max-w-md px-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={e => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
        {hiddenArchived > 0 && (
          <button
            onClick={() => setShowArchived(true)}
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground whitespace-nowrap"
          >
            {hiddenArchived} price{hiddenArchived === 1 ? '' : 's'} hidden
          </button>
        )}
      </div>

      {/* Catalog tree */}
      <div className="space-y-3">
        {filteredCatalog.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {search
              ? `No results matching "${search}"`
              : hiddenArchived > 0
                ? 'Everything in this catalog is archived — turn on "Show archived" to see it.'
                : 'No catalog data available'}
          </div>
        ) : (
          filteredCatalog.map(family => (
            <FamilyRow
              key={family.id}
              family={family}
              canEdit={canEdit}
              onEditPrice={(price) => setModal({ mode: 'edit-price', price })}
              onCreatePrice={(item) => setModal({ mode: 'create-price', item })}
              onEditItem={(item) => setModal({ mode: 'edit-item', item })}
            />
          ))
        )}
      </div>

      {/* In release mode a save only touches the draft store, so there is
          nothing new to fetch — refetching would just re-show current state. */}
      <PricingModal
        modal={modal}
        mode={pricingMode}
        onClose={() => setModal(null)}
        onSaved={pricingMode === 'release' ? () => {} : fetchData}
      />
    </div>
  )
}
